/**
 * Directory match (client side)
 * ------------------------------
 * Pure helpers for matching a document's party against the sitewide business
 * directory. Normalization mirrors the server (jobs/directoryIngest.normalizeName) so
 * client previews and server dedup agree. The network call lives in ProcessReceiptsTab;
 * here we only normalize and pick the best server candidate. Unit-tested.
 */

export interface DirectoryCandidate {
  busi_id: number | string;
  country_id?: number;
  canonical_name: string;
  city_id?: number | null;
  date_format_id?: number | null;
  sim?: number;            // pg_trgm similarity from /api/directory/match
}

/** Same rules as the server normalizeName + filingAutoMatch.normalizeText. */
export function normalizeBusinessName(s: string): string {
  return String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Pick the best directory candidate for a name. Returns the candidate only when its
 * similarity clears `minSim` (or its normalized name equals the query exactly).
 */
export function pickBestCandidate(
  candidates: DirectoryCandidate[],
  name: string,
  minSim = 0.6,
): DirectoryCandidate | null {
  if (!candidates || !candidates.length) return null;
  const norm = normalizeBusinessName(name);
  if (!norm) return null;
  const exact = candidates.find(c => normalizeBusinessName(c.canonical_name) === norm);
  if (exact) return exact;
  const ranked = [...candidates].sort((a, b) => (Number(b.sim) || 0) - (Number(a.sim) || 0));
  const top = ranked[0];
  return top && (Number(top.sim) || 0) >= minSim ? top : null;
}
